import { useState } from 'react';
import toast from 'react-hot-toast';
import { FaTimes } from 'react-icons/fa';

import { Button, Input, Select } from '@client/toolkit';

import { OnboardingUser } from '../onboarding.types';
import { OnboardingActions } from '../onboarding-actions';

// Simple RFC5322-subset check — good enough for a UI-level "is this shaped like an email"
// guard. The server will still do the canonical validation before creating the invitation.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface StepBrukereProps {
  users: OnboardingUser[];
  onUsersChange: (users: OnboardingUser[]) => void;
  onBack: () => void;
  onNext: () => void;
}

export function StepBrukere({ users, onUsersChange, onBack, onNext }: StepBrukereProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OnboardingUser['role']>('Bruker');

  const handleAdd = () => {
    const trimmed = email.trim().toLowerCase();

    if (!trimmed) {
      toast.error('Skriv inn en e-postadresse', { id: 'onboarding-user-email' });
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      toast.error('Ugyldig e-postadresse', { id: 'onboarding-user-email' });
      return;
    }
    if (users.some((u) => u.email === trimmed)) {
      toast.error('Denne e-postadressen er allerede lagt til', { id: 'onboarding-user-email' });
      return;
    }

    onUsersChange([
      ...users,
      { id: crypto.randomUUID(), email: trimmed, role },
    ]);
    setEmail('');
  };

  const handleRemove = (id: string) => {
    onUsersChange(users.filter((u) => u.id !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Legg til brukere</h2>
      <p className="text-white/60 mb-8">
        Inviter kollegaer til å bruke Exio Portfolio. Du kan også gjøre dette senere.
      </p>

      <div className="flex gap-3 items-stretch">
        <div className="flex-1">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="E-postadresse"
            className="!h-full"
          />
        </div>
        <Select
          value={role}
          onChange={(e) => setRole(e.target.value as OnboardingUser['role'])}
          className="!w-36"
        >
          <option value="Bruker">Bruker</option>
          <option value="Administrator">Administrator</option>
        </Select>
        <Button color="primary" size="md" onClick={handleAdd}>
          Legg til
        </Button>
      </div>

      <div className="mt-6 space-y-3">
        {users.map((user) => (
          <div
            key={user.id}
            className="flex items-center gap-4 p-4 rounded-lg border border-border bg-surface-base"
          >
            <div className="w-10 h-10 rounded-full bg-surface-elevated flex items-center justify-center text-white font-semibold text-sm">
              {user.email[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium truncate">{user.email}</div>
              <div className="text-white/50 text-sm">
                {user.role === 'Administrator' ? 'Administrator' : 'Bruker (kun visning)'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleRemove(user.id)}
              className="text-white/40 hover:text-white transition-colors p-1"
            >
              <FaTimes />
            </button>
          </div>
        ))}
      </div>

      <OnboardingActions onBack={onBack} onNext={onNext} />
    </div>
  );
}
